import {
  fetchManifest,
  findModel,
  textureUrl,
  type ModelVariable,
  type RunManifest,
} from '../../api/models'
import type { Layer } from '../layer'
import {
  DataTextureLayer,
  maxTextureSize,
  type Bbox3857,
  type TextureFrame,
} from './dataTextureLayer'
import { variableRamp } from './ramp'

/**
 * One model field on the map: `DataTextureLayer` wired to a run manifest.
 *
 * The axis here is a *forecast* axis — hourly, running forward from the run —
 * so the frame budget is taken from the near end the way NDFD's is. Frames
 * whose valid time is already past are still real frames (f00 of a two-hour-old
 * run is two hours ago) and the shared axis sorts them in with everything else;
 * the map menu already prints a layer's own instant when it differs from the
 * slider's, which is exactly the case of a four-minute radar loop next to an
 * hourly model.
 *
 * Nothing in this file knows what a dBZ is. Units, decimals, the plotted
 * range, the floor and the colours all come from `/api/models`, because the
 * backend registry is what coloured the pixels.
 */

/**
 * Which rendered width to ask for. Set once at boot from the layout, the same
 * way the frame budget is: a full-plate quad has no viewport culling, so a
 * phone pulling the 2048 px plates would carry a CONUS texture it cannot see.
 * The manifest lists what actually exists and the closest one to this wins, so
 * a deployment that renders other widths needs no change here.
 */
let widthTarget = 2048

export function setModelWidthTarget(px: number): void {
  widthTarget = Math.max(1, px)
}

export function modelWidthTarget(): number {
  return widthTarget
}

/**
 * The lead window and the chosen run, as module state.
 *
 * The same seam `widthTarget` uses, and for the same reason: `lib/map/` is
 * plain TypeScript with no Svelte imports, so the runes in
 * `state/models.svelte.ts` push *in* rather than being read *out*. `MapView`
 * is the one place that calls these, and it rebuilds the model layers
 * afterwards — both values are read when a layer is created, because a frame
 * list is fetched at `load()` and there is nothing to mutate later.
 */
let leadWindow: LeadWindow = '12h'
let runOverrides: Record<string, string> = {}

export function setModelWindow(window: LeadWindow): void {
  leadWindow = window
}

export function modelWindow(): LeadWindow {
  return leadWindow
}

export function setModelRuns(runs: Record<string, string>): void {
  runOverrides = { ...runs }
}

export function modelRun(modelId: string): string | null {
  return runOverrides[modelId] ?? null
}

/**
 * Which rendered width to ask for: the one closest to the target **by ratio**,
 * not by difference.
 *
 * Resolution is multiplicative — half the target is as wrong as twice it — and
 * a linear distance does not say that. Measured the moment Stage 6.2 raised
 * NBM to 3584: against a desktop target of 2048, `|1024 - 2048|` is 1024 and
 * `|3584 - 2048|` is 1536, so the nearest-by-difference rule handed the
 * desktop the **phone** plate and quietly undid the raise. By ratio 3584 is
 * 1.75x the target where 1024 is 2x away, and the right one wins.
 *
 * It matters in the other direction too, which is why this is not simply
 * "the smallest width at or above the target": a phone asking for 1024 out of
 * `[512, 4096]` must get 512, because the plate is one resident texture
 * whatever the viewport shows and 4096 is a CONUS texture it cannot see. Ratio
 * gets both ends.
 */
export function pickWidth(
  widths: readonly number[],
  target = widthTarget,
  ceiling: number | null = null,
): number | null {
  if (widths.length === 0) return null
  // **A width the driver will refuse is not a width.** WebGL2 guarantees only
  // `MAX_TEXTURE_SIZE` 2048, and HRRR and NAM ship 2816 while NBM ships 3584.
  // This is not new — the `image` source uploads the same plate today and
  // fails the same way — but the data texture is the first thing here that can
  // *ask*, so it falls back to the next width down instead of uploading
  // something that comes back blank with no diagnosis. If every width is over
  // the ceiling, the smallest is the best available answer.
  const allowed = ceiling === null ? widths : widths.filter((w) => w <= ceiling)
  const usable = allowed.length > 0 ? allowed : [Math.min(...widths)]
  const distance = (w: number) => Math.abs(Math.log(w / target))
  return [...usable].sort((a, b) => distance(a) - distance(b) || b - a)[0]
}

/**
 * How far ahead a model layer looks, as the picker offers it.
 *
 * `pickForecastSteps` used to take twelve *consecutive* frames from the near
 * end, which is right for HRRR and absurd for the models Phase 4 adds: the
 * first twelve hours of GFS's sixteen-day forecast, the first twelve of NBM's
 * eleven days. The rest of the run was ingested, rendered, stored — and
 * unreachable. The window is what makes the frame budget mean something.
 */
export const LEAD_WINDOWS = {
  // +3 h exists for the 15-minute product: twelve frames of it is three hours
  // at full resolution, where +6 h would stride it back to half-hourly and
  // throw away the reason to draw it.
  '3h': 180,
  '6h': 360,
  '12h': 720,
  '48h': 2880,
  all: Infinity,
} as const

export type LeadWindow = keyof typeof LEAD_WINDOWS

export const LEAD_WINDOW_IDS = Object.keys(LEAD_WINDOWS) as LeadWindow[]

export function isLeadWindow(value: unknown): value is LeadWindow {
  return typeof value === 'string' && value in LEAD_WINDOWS
}

/**
 * The frames to draw: inside the window, from the near end, strided to the
 * budget.
 *
 * `count` is the model frame budget — `FRAMES_MODEL_*`, not the nowCOAST one.
 * Phase 2's justification for that number ("every GetMap is a live render at
 * NOAA's end") does not transfer, and neither does its size: these are our own
 * immutable PNGs on one swapped texture, so the budget is set high enough that
 * a default deployment never strides at all and only a raised
 * `WEATHER_MODEL_MAX_LEAD_MINUTES` reaches it. What survives is bandwidth
 * while playing, which the *window* bounds, not this.
 *
 * **Striding is honest for an instantaneous field and dishonest for an
 * accumulation.** Every fourth hour of a temperature forecast is a temperature
 * forecast with fewer frames, and the slider labels the instant it is showing.
 * Every fourth hour of an *hourly rainfall* field silently drops three
 * quarters of the rain: the animation understates the storm and nothing on
 * screen says so. So a variable with an accumulation window sets
 * `stridable: false` and its window shrinks instead of its sampling — fewer
 * hours, all of them.
 *
 * A *step* is keyed on lead minutes, not on the forecast hour, because no
 * model's axis is `range(hours)` any more: GFS goes 3-hourly after f120, NBM
 * starts at f001, and HRRR's sub-hourly product steps every 15 minutes.
 */
export function pickForecastSteps(
  steps: readonly { lead: number; valid: string }[],
  count: number,
  now = Date.now(),
  options: { window?: LeadWindow; stridable?: boolean } = {},
): { lead: number; time: Date }[] {
  const parsed = steps
    .map((s) => ({ lead: s.lead, time: new Date(s.valid) }))
    .filter((h) => !Number.isNaN(h.time.getTime()))
    .sort((a, b) => a.time.getTime() - b.time.getTime())
  if (parsed.length === 0 || count < 1) return []

  // Open on what is happening: the last frame at or before now.
  const ahead = parsed.findIndex((h) => h.time.getTime() > now)
  const anchor = ahead < 0 ? parsed.length - 1 : Math.max(0, ahead - 1)

  // The window is measured from the frame at `anchor`, not from the run: "look
  // ahead +6 h" means six hours from now, and a run that is already three
  // hours old has spent none of them.
  //
  // The anchor is deliberately *not* pulled back to `length - count` to
  // guarantee the budget can be filled, which is what it used to do. That
  // clamp was invisible while the budget was 12 and every run had far more
  // steps than that; at a model budget of 96 it fires on every run, drags the
  // anchor to 0, and re-bases the window on the run instead of on now — so
  // "+6 h" on a three-hour-old HRRR run would show the three hours that have
  // already happened. Underfilling the budget is the correct answer to a
  // narrow window: the window bounds the selection and the budget only strides
  // it down.
  const limit = LEAD_WINDOWS[options.window ?? 'all']
  const from = parsed[anchor].lead
  const tail = parsed.slice(anchor)
  let chosen = tail.filter((s) => s.lead - from <= limit)

  // Backing up is for a run with nothing left ahead of it, not for a narrow
  // window. `chosen.length < tail.length` says frames exist past the window,
  // which means the window is what bound the selection and there is nothing to
  // fix; otherwise the selection ran off the end of the run and the *last*
  // `count` frames inside the window are the right ones, because the first
  // would be the oldest hours of a run nobody is watching any more.
  if (chosen.length === tail.length && chosen.length < count) {
    const last = parsed[parsed.length - 1].lead
    chosen = parsed.filter((s) => last - s.lead <= limit).slice(-count)
  }
  if (chosen.length <= count) return chosen

  if (options.stridable === false) {
    // Shrink the window rather than the sampling: an hour of rain that is not
    // drawn is rain the loop says did not fall.
    return chosen.slice(0, count)
  }
  return evenlySpaced(chosen, count)
}

/**
 * Exactly `count` frames spanning the whole list, ends included.
 *
 * Evenly spaced rather than a fixed `stride`, because `Math.ceil(n / count)`
 * throws frames away for nothing: seven steps at a budget of six is a stride
 * of two and *four* frames drawn. The budget is a budget, not a ceiling to
 * come in under.
 */
function evenlySpaced<T>(items: T[], count: number): T[] {
  if (items.length <= count) return items
  if (count === 1) return [items[0]]
  const step = (items.length - 1) / (count - 1)
  const out: T[] = []
  for (let i = 0; i < count; i++) out.push(items[Math.round(i * step)])
  return out
}

export interface ModelLayerOptions {
  id: string
  model: string
  variable: string
  attribution: string
  opacity: number
  frameCount: number
  /** How far ahead to look. Read at `load()`, which is why changing it
   *  rebuilds the layer rather than mutating it. */
  window?: LeadWindow
  /**
   * The run to draw, or null for the catalog's `latest`.
   *
   * Session-scoped and never persisted: retention deletes a run within hours,
   * so a restored selection would boot the app pointed at a 404.
   */
  run?: string | null
  /** Tests pass a fixed clock; nothing else does. */
  now?: () => number
}

/** What one run gives this layer, whichever plate kind it then draws with. */
interface Resolved {
  run: string
  manifest: RunManifest
  variable: ModelVariable | undefined
  attribution: string
  width: number
  steps: { lead: number; time: Date }[]
}

export class ModelLayer implements Layer {
  readonly id: string
  /**
   * The layer that actually draws, chosen from the run's own `plates` list.
   *
   * Null until `load()` resolves, because **which kind to use is the run's
   * answer, not the deployment's**: a run ingested before the migration has a
   * paletted plate and no texture, and one ingested after has both. Picking
   * from the manifest is what keeps an old run viewable and what makes
   * dropping the palette a deletion rather than a cutover.
   */
  private inner: Layer | null = null
  /**
   * The run these frames came from, as `/api/point` wants it.
   *
   * The *URL segment* (`20260918T00Z`), not the manifest's ISO `run` field:
   * they name the same instant and only one of them is a path.
   */
  private run: string | null = null
  private manifest: RunManifest | null = null
  /** Valid-time -> lead minutes, so a readout asks about the frame on screen. */
  private byTime = new Map<number, number>()
  private loaded: Promise<void> | null = null

  constructor(private readonly opts: ModelLayerOptions) {
    this.id = opts.id
  }

  get times(): readonly Date[] {
    return this.inner?.times ?? []
  }

  currentTime(): Date | null {
    return this.inner?.currentTime() ?? null
  }

  /** The run on screen, or null before `load()` lands or when none is servable. */
  currentRun(): string | null {
    return this.run
  }

  /**
   * Who to credit for the pixels on screen — the *catalog's* answer, not the
   * registry row's, because one layer kind draws six models and a constant in
   * the row credited HRRR under a GFS plate.
   *
   * Read by `Legend.svelte` rather than by MapLibre's `AttributionControl`,
   * which reads `attribution` off **sources in use** and a custom layer has
   * none. A dummy source does not help either: nothing marks it `used`. So the
   * app shows the credit itself, under the colour bar that is already on
   * screen whenever a model layer is active.
   */
  ownAttribution(): string | null {
    // Delegated, not answered: the paletted path puts the credit on its own
    // `image` source and MapLibre draws it, so answering here as well would
    // print it twice through the migration.
    return this.inner?.ownAttribution?.() ?? null
  }

  /**
   * The lead, in minutes, the layer is *drawing*, resolved through the
   * manifest.
   *
   * A readout must describe the pixel on screen, so it asks with this rather
   * than with the slider's position — the same discipline the WMS branch needs
   * for a different reason, and here the mapping is exact because the frames
   * are ours.
   */
  currentLead(): number | null {
    const time = this.currentTime()
    return time ? (this.byTime.get(time.getTime()) ?? null) : null
  }

  /**
   * The run, its steps and the width to ask for — everything that does not
   * depend on which plate kind draws them.
   */
  private async resolve(): Promise<Resolved | null> {
    const info = await findModel(this.opts.model)
    if (!info) return null
    // `latest` is the newest *servable* run, not the newest directory: a run
    // three hours into its publication is listed but is not something to
    // animate. A chosen run overrides it, and one the catalog no longer lists
    // falls back rather than 404ing every frame — retention can delete it
    // between the picker being opened and a layer being switched on.
    const chosen = this.opts.run
    const run = chosen && info.runs.some((r) => r.run === chosen) ? chosen : info.latest
    if (!run) return null

    const manifest = await fetchManifest(this.opts.model, run)
    const entry = manifest?.variables?.[this.opts.variable]
    if (!manifest || !entry?.steps?.length) return null

    // The driver's ceiling, not just the target: a plate wider than
    // `MAX_TEXTURE_SIZE` uploads as nothing at all, and the fallback is one
    // width down rather than a blank map with no diagnosis.
    const width = pickWidth(manifest.grid?.widths ?? info.widths, widthTarget, maxTextureSize())
    if (width === null) return null

    // An accumulation is not stridable: skipping three hours of four makes the
    // loop understate the total, and nothing on screen would say so. The
    // catalog states the window; this file does not know what a field is.
    const variable = info.variables.find((v) => v.id === this.opts.variable)
    const picked = pickForecastSteps(
      entry.steps,
      this.opts.frameCount,
      this.opts.now?.() ?? Date.now(),
      { window: this.opts.window, stridable: (variable?.window_minutes ?? 0) === 0 },
    )
    if (picked.length === 0) return null

    this.run = run
    this.manifest = manifest
    this.byTime = new Map(picked.map((step) => [step.time.getTime(), step.lead]))
    return {
      run,
      manifest,
      variable,
      // The catalog's, not the row's: one layer kind now draws several models
      // and a constant in the registry row credited HRRR for a GFS plate.
      // Caught by looking at a screenshot, which is what `make e2e` is for.
      attribution: info.attribution,
      width,
      steps: picked,
    }
  }

  /**
   * Build the layer that draws, from what the *run* holds.
   *
   * A run states its plate kinds and its decode rule in its own manifest, and
   * nothing here reads `WEATHER_MODEL_PLATES`: the setting says what the
   * worker writes from now on, and a run on disk says what it has. A run
   * rendered before the data texture — or one whose manifest carries no decode
   * rule for this field — has nothing this app can draw, so it draws nothing
   * and says so, rather than guessing a range and painting right-looking
   * colours over wrong numbers.
   *
   * That split is what made the migration a migration. It is kept because the
   * next plate kind arrives the same way.
   */
  private build(found: Resolved): Layer | null {
    const decode = found.manifest.variables?.[this.opts.variable]?.texture
    const plates = found.manifest.plates ?? ['palette']
    if (!plates.includes('texture') || !decode || !found.variable) {
      console.warn(
        `${this.id}: run ${found.run} holds no data texture for ${this.opts.variable}`,
      )
      return null
    }
    const frames: TextureFrame[] = found.steps.map((step) => ({
      time: step.time,
      url: textureUrl(this.opts.model, found.run, this.opts.variable, found.width, step.lead),
    }))
    // The ramp is built from the catalog's colormap across **the run's own**
    // lo/hi, not the catalog's `plotted`: the plate was quantised with the
    // former, and a `plotted` edit since then would otherwise paint
    // right-looking colours over wrong numbers.
    const ramp = variableRamp(found.variable, decode)
    return new DataTextureLayer({
      id: this.opts.id,
      attribution: this.opts.attribution,
      opacity: this.opts.opacity,
      loadFrames: async () => ({
        frames,
        bbox3857: found.manifest.grid.bbox3857 as Bbox3857,
        ramp,
        nodata: decode.nodata,
        attribution: found.attribution,
      }),
    })
  }

  /** The manifest behind the frames on screen; the status panel reads it. */
  currentManifest(): RunManifest | null {
    return this.manifest
  }

  load(): Promise<void> {
    this.loaded ??= this.resolve()
      .then(async (found) => {
        if (!found) return
        const inner = this.build(found)
        if (!inner) return
        await inner.load()
        this.inner = inner
      })
      .catch((e) => {
        // A degraded path a user is meant to notice gets a console line at
        // least: an empty model overlay is indistinguishable from a quiet day.
        console.warn(`${this.id}: could not load model frames`, e)
      })
    return this.loaded
  }

  add(map: Parameters<Layer['add']>[0], beforeId?: string): void {
    this.inner?.add(map, beforeId)
  }

  remove(map: Parameters<Layer['remove']>[0]): void {
    this.inner?.remove(map)
  }

  setOpacity(opacity: number): void {
    this.inner?.setOpacity(opacity)
  }

  setTime(time: Date): void {
    this.inner?.setTime(time)
  }

  setVisible(visible: boolean): void {
    this.inner?.setVisible(visible)
  }

  ready(): Promise<void> {
    return this.inner?.ready() ?? Promise.resolve()
  }
}
